import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { useAddContractualManagerTag, useOrganisationUsers } from '../../hooks';
import { getUserDisplayName } from '../../utils/contractual-managers';
import type { User } from '../../types';

interface AddExistingContractualManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** MongoDB organisation id of the quote's customer. */
  organisationId: string;
  /** Contacts already offered by the combobox — shown with a badge, still selectable. */
  taggedIds: Set<string>;
  onAdded: (user: User) => void;
}

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Promote an EXISTING contact of the account to Contractual Manager (#1121).
 *
 * Sibling of MiniUserFormDialog, which creates a brand-new contact. This one
 * lists every contact of the organisation — not just those already tagged — and
 * applies the tag to the picked one, which is what previously forced a detour
 * through v1.atek.io.
 *
 * The search is sent to the server rather than filtered in the browser: the
 * underlying query is capped at 100 rows, so a client-side filter would silently
 * hide contacts on large accounts.
 */
export function AddExistingContractualManagerDialog({
  open,
  onOpenChange,
  organisationId,
  taggedIds,
  onAdded,
}: AddExistingContractualManagerDialogProps) {
  const { t } = useTranslation('quotes');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const addTag = useAddContractualManagerTag();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setDebouncedSearch('');
    }
  }, [open]);

  // No tag filter: every contact of the organisation is a candidate.
  const { data: candidates = [], isLoading } = useOrganisationUsers(
    open ? organisationId || undefined : undefined,
    undefined,
    debouncedSearch,
  );

  const sorted = useMemo(
    () => [...candidates].sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b))),
    [candidates],
  );

  const handleSelect = async (user: User) => {
    try {
      await addTag.mutateAsync({ userId: user._id, orgId: organisationId });
      onAdded(user);
      toast.success(t('contractualManager.addSuccess'));
      onOpenChange(false);
    } catch (error) {
      toast.error(t('contractualManager.addError'), {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('contractualManager.pickerTitle')}</DialogTitle>
          <DialogDescription>{t('contractualManager.pickerDescription')}</DialogDescription>
        </DialogHeader>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('contractualManager.pickerSearch')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading ? t('contractualManager.pickerLoading') : t('contractualManager.pickerEmpty')}
            </CommandEmpty>
            <CommandGroup>
              {sorted.map((user) => (
                <CommandItem
                  key={user._id}
                  value={user._id}
                  disabled={addTag.isPending}
                  onSelect={() => handleSelect(user)}
                >
                  <div className="flex w-full items-center justify-between gap-2">
                    <div className="flex flex-col">
                      <span>{getUserDisplayName(user)}</span>
                      {user.email ? (
                        <span className="text-xs text-muted-foreground">{user.email}</span>
                      ) : null}
                    </div>
                    {taggedIds.has(user._id) ? (
                      <Badge variant="secondary">{t('contractualManager.alreadyManager')}</Badge>
                    ) : null}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
